import { lazy, Suspense, useLayoutEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";

import AuthGuard from "./auth/AuthGuard";

/* Public */
import Home from "./pages/Home";
import Login from "./pages/Login";

/* Account */

/* Owner */

/* Admin */

import AltingDriversList from "./pages/AltingDriversList";

import CarDetail from "./pages/CarDetail";

    

import NotFound from "./pages/NotFound";

/* ── Everything below is fetched on demand ──────────────────────────────────
   These are the screens a given person will mostly never open: the admin panels,
   the partner dashboards and onboarding forms, and the pages reached from a menu
   rather than on arrival. Compiled into the main bundle they cost every visitor
   who just wants a cab - 515 KB gzipped and 3.7 seconds to ready, measured live.

   Each becomes its own file, requested only when somebody navigates to it. The
   Suspense boundary below is what renders while that file is in flight. */
const PartnerLogin = lazy(() => import("./pages/PartnerLogin"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const TripPage = lazy(() => import("./pages/TripPage"));
const TripsIndex = lazy(() => import("./pages/TripsIndex"));
// Pages for Google by city and car type (mock Seo_3).
const SeoCabsPage = lazy(() => import("./pages/SeoCabsPage"));
const SeoDriversPage = lazy(() => import("./pages/SeoDriversPage"));
const SeoHubPage = lazy(() => import("./pages/SeoHubPage"));
const CabPage = lazy(() => import("./pages/CabPage"));
const SeoTripHubPage = lazy(() => import("./pages/SeoTripHubPage"));
const RateLinkPage = lazy(() => import("./pages/RateLinkPage"));
const TeRoute = lazy(() => import("./components/seo/TeRoute"));
const SharePage = lazy(() => import("./pages/SharePage"));
const AccountDelete = lazy(() => import("./pages/AccountDelete"));
const DeletionPending = lazy(() => import("./pages/DeletionPending"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Support = lazy(() => import("./pages/Support"));
const Settings = lazy(() => import("./pages/Settings"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const AdminPriceController = lazy(() => import("./pages/AdminPriceController.jsx"));
const AdminCities = lazy(() => import("./pages/AdminCities.jsx"));
// HR portal (31 Oct 2026, mock HR_1): Super Admin pages and each person's own My HR.
const AdminHr = lazy(() => import("./pages/AdminHr.jsx"));
const AdminHrNew = lazy(() => import("./pages/AdminHrNew.jsx"));
const AdminHrPerson = lazy(() => import("./pages/AdminHrPerson.jsx"));
const MyHr = lazy(() => import("./pages/MyHr.jsx"));
const AdminDocNames = lazy(() => import("./pages/AdminDocNames.jsx"));
const AdminRecommended = lazy(() => import("./pages/AdminRecommended.jsx"));
const AdminServices = lazy(() => import("./pages/AdminServices.jsx"));
const VendorPage = lazy(() => import("./pages/VendorPage.jsx"));
const AdminPlatformConfig = lazy(() => import("./pages/AdminPlatformConfig.jsx"));
const AdminFeeManagement = lazy(() => import("./pages/AdminFeeManagement.jsx"));
const Account = lazy(() => import("./pages/Account"));
const Wallet = lazy(() => import("./pages/Wallet"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const OwnerDrivers = lazy(() => import("./pages/OwnerDrivers"));
const CabOwnerDashboard = lazy(() => import("./pages/CabOwnerDashboard"));
const CabOwnerProfileEdit = lazy(() => import("./pages/CabOwnerProfileEdit"));
const EditCabAccount = lazy(() => import("./pages/EditCabAccount"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminCarApproval = lazy(() => import("./pages/AdminCarApproval"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const AdminAuditEntry = lazy(() => import("./pages/AdminAuditEntry"));
const AdminAccounts = lazy(() => import("./pages/AdminAccounts"));
const AdminAccountDetail = lazy(() => import("./pages/AdminAccountDetail"));
const AdminAccountNew = lazy(() => import("./pages/AdminAccountNew"));
const AdminRecycleBin = lazy(() => import("./pages/AdminRecycleBin"));
const CabOwnerOnboarding = lazy(() => import("./pages/CabOwnerOnboarding"));
const AgentOnboarding = lazy(() => import("./pages/AgentOnboarding"));
const AdminSearch = lazy(() => import("./pages/AdminSearch"));
const AdminOnboardings = lazy(() => import("./pages/AdminOnboardings"));
const AdminOnboardingDetail = lazy(() => import("./pages/AdminOnboardingDetail"));
const AdminCabModifications = lazy(() => import("./pages/AdminCabModifications"));
const AdminChangeDetail = lazy(() => import("./pages/AdminChangeDetail"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminLeadDetail = lazy(() => import("./pages/AdminLeadDetail"));
const AdminWalletPoints = lazy(() => import("./pages/AdminWalletPoints"));
const AdminWalletDetail = lazy(() => import("./pages/AdminWalletDetail"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminAnalyticsSession = lazy(() => import("./pages/AdminAnalyticsSession"));
const AdminWhatsAppSessions = lazy(() => import("./pages/AdminWhatsAppSessions"));
const AdminOwners = lazy(() => import("./pages/AdminOwners"));
const AdminOwnerDetail = lazy(() => import("./pages/AdminOwnerDetail"));
const AdminCarDivergence = lazy(() => import("./pages/AdminCarDivergence"));
const AltingDriverOnboarding = lazy(() => import("./pages/AltingDriverOnboarding"));
const Join = lazy(() => import("./pages/Join"));
const Desk = lazy(() => import("./pages/Desk"));
const DeskApplication = lazy(() => import("./pages/DeskApplication"));
const DeskWhatsApp = lazy(() => import("./pages/DeskWhatsApp"));
const DeskWhatsAppThread = lazy(() => import("./pages/DeskWhatsAppThread"));
const AltingDriverProfile = lazy(() => import("./pages/AltingDriverProfile"));
const AltingDriverDashboard = lazy(() => import("./pages/AltingDriverDashboard"));
const AdminAltingDrivers = lazy(() => import("./pages/AdminAltingDrivers"));
const AdminAltingDriverDetail = lazy(() => import("./pages/AdminAltingDriverDetail"));
const MoreServices = lazy(() => import("./pages/MoreServices"));
const SetUsername = lazy(() => import("./pages/SetUsername"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const LocationPicker = lazy(() => import("./pages/LocationPicker"));
const HireLocationPicker = lazy(() => import("./pages/HireLocationPicker"));
const NoCabs = lazy(() => import("./pages/NoCabs"));
const RequestQuotation = lazy(() => import("./pages/RequestQuotation"));



/* Shown while an on-demand screen is being fetched. Kept plain and centred: a
   loud full-page spinner that appears for two hundred milliseconds reads as a
   fault rather than as progress. */
function RouteLoading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
      <div className="w-6 h-6 border-2 border-[#7BAE00] border-t-transparent rounded-full animate-spin" />
      <span className="sr-only">Loading</span>
    </div>
  );
}
/* A new page opens at its top. The router leaves the window where the last page was,
   so a tap far down a long list - a car's card, "See price" - opened the next page
   (the trip form, a car) scrolled to its end. Back and forward are left to the
   browser, which returns the reader to where they were. The hash is part of the key
   because a car's address carries its id there ("/car/CAR#<uuid>"); a change of query
   alone (a filter) is not a new page and keeps its place. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  // Read, not watched: a filter's replace on the same page must not count as a move.
  const navType = useRef(null);
  navType.current = useNavigationType();
  useLayoutEffect(() => {
    // "instant": the page's CSS scrolls smoothly, and a new page must not slide up
    // from the old one's position - it simply starts at its top.
    if (navType.current !== "POP") window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

export default function AppRoutes() {
  return (
      <Suspense fallback={<RouteLoading />}>
      <ScrollToTop />
      <Routes>
        {/* ================= PUBLIC ================= */}
        <Route path="/" element={<Home />} />
        <Route path="/car/:id" element={<CarDetail />} />
        {/* A season trip from Vijayawada (Oct 2026) */}
        <Route path="/trips" element={<TripsIndex />} />
        <Route path="/trips/:slug" element={<TripPage />} />
        {/* A trip by car type, and from Guntur; each car's own address (mock Seo_8) */}
        <Route path="/trips/from-guntur/:slug" element={<TripPage origin="guntur" />} />
        <Route path="/trips/:slug/:type" element={<TripPage />} />
        <Route path="/cab/:city/:slug" element={<CabPage />} />
        {/* Fare charts, airport taxi, one way drops, and the Telugu pages (mock Seo_12) */}
        <Route path="/taxi-fare/:city" element={<SeoTripHubPage kind="fare" />} />
        <Route path="/airport-taxi/:city" element={<SeoTripHubPage kind="airport" />} />
        <Route path="/drop-taxi/:city" element={<SeoTripHubPage kind="drop" />} />
        {/* The 5 parts from WhatsApp's "Rate in detail" - no login (mock Rate5_1 change 3). */}
        <Route path="/rate/:ref" element={<RateLinkPage />} />
        {/* Season pages (30 Oct 2026, mock Seo_BF change 3). */}
        <Route path="/season/:season" element={<SeoTripHubPage kind="season" />} />
        <Route path="/wedding-cars/:city" element={<SeoTripHubPage kind="season" />} />
        <Route path="/wedding-tempo/:city" element={<SeoTripHubPage kind="season" />} />
        <Route path="/te/season/:season" element={<TeRoute><SeoTripHubPage kind="season" /></TeRoute>} />
        <Route path="/te/wedding-cars/:city" element={<TeRoute><SeoTripHubPage kind="season" /></TeRoute>} />
        <Route path="/te/wedding-tempo/:city" element={<TeRoute><SeoTripHubPage kind="season" /></TeRoute>} />
        <Route path="/te/cabs" element={<TeRoute><SeoHubPage kind="cabs" /></TeRoute>} />
        <Route path="/te/cabs/:city" element={<TeRoute><SeoCabsPage /></TeRoute>} />
        <Route path="/te/cabs/:city/:type" element={<TeRoute><SeoCabsPage /></TeRoute>} />
        <Route path="/te/hire-drivers" element={<TeRoute><SeoHubPage kind="drivers" /></TeRoute>} />
        <Route path="/te/hire-drivers/:city" element={<TeRoute><SeoDriversPage /></TeRoute>} />
        <Route path="/te/trips/:slug" element={<TeRoute><TripPage /></TeRoute>} />
        <Route path="/te/taxi-fare/:city" element={<TeRoute><SeoTripHubPage kind="fare" /></TeRoute>} />
        <Route path="/te/airport-taxi/:city" element={<TeRoute><SeoTripHubPage kind="airport" /></TeRoute>} />
        <Route path="/te/drop-taxi/:city" element={<TeRoute><SeoTripHubPage kind="drop" /></TeRoute>} />
        {/* Cabs and hire drivers by city and car type, for Google (mock Seo_3) */}
        <Route path="/cabs" element={<SeoHubPage kind="cabs" />} />
        <Route path="/cabs/:city" element={<SeoCabsPage />} />
        <Route path="/cabs/:city/:type" element={<SeoCabsPage />} />
        <Route path="/hire-drivers" element={<SeoHubPage kind="drivers" />} />
        <Route path="/hire-drivers/:city" element={<SeoDriversPage />} />
        {/* An owner's or hire driver's own link for their customers (Oct 2026) */}
        <Route path="/o/:code" element={<SharePage />} />

        <Route path="/login" element={<Login />} />
        <Route path="/partner-login" element={<PartnerLogin />} />
        {/* Field agent application. Public, like the endpoint behind it. */}
        <Route path="/agent-apply" element={<AgentOnboarding />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        
        <Route path="/support" element={<Support />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/request-quotation" element={<RequestQuotation />} />
        
        <Route path="/location-picker" element={<LocationPicker />} />
        <Route path="/hire-location-picker" element={<HireLocationPicker />} />
        <Route path="/no-cabs" element={<NoCabs />} />
        
        <Route path="/set-display-name" element={<SetUsername />} />
        <Route path="/set-password" element={<SetPassword />} />
        
        {/* One link for ads (Oct 2026): car owner or hire driver, with ?lang= and ?code=. */}
        <Route path="/join" element={<Join />} />
        {/* Car wash and PDI businesses (mock Svc_2). */}
        <Route path="/vendor" element={<VendorPage />} />
        <Route path="/cab-owner/onboarding" element={<CabOwnerOnboarding />} />
        <Route path="/alting-driver/onboarding" element={<AltingDriverOnboarding />} />
        <Route path="/alt-driver/onboarding" element={<Navigate to="/alting-driver/onboarding" replace />} />
        
        <Route path="/drivers" element={<AltingDriversList />} />
        <Route path="/drivers/:driverId" element={<AltingDriverProfile />} />
        
        <Route path="/services" element={<MoreServices />} />
        <Route path="/services/*" element={<MoreServices />} />

        {/* ================= PROTECTED (ANY LOGGED IN) ================= */}
        <Route element={<AuthGuard />}>
          <Route path="/account" element={<Account />} />
          <Route path="/account/delete" element={<AccountDelete />} />
          <Route path="/account/deletion-pending" element={<DeletionPending />} />
          <Route path="/hr/me" element={<MyHr />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/notifications" element={<Notifications />} />
        </Route>

        {/* ================= OWNER ================= */}
        <Route element={<AuthGuard allowedRoles={["OWNER", "CAB_OWNER", "ADMIN", "SUPER_ADMIN"]} />}>
          <Route path="/owner" element={<CabOwnerDashboard />} />
          <Route path="/owner/legacy" element={<OwnerDashboard />} />
          {/* The old separate car list: the My Cars tab is the one place for cars now. */}
          <Route path="/owner/cars" element={<Navigate to="/owner" replace />} />
          <Route path="/owner/add-car" element={<CabOwnerOnboarding isAddCarMode={true} />} />
          <Route path="/owner/drivers" element={<OwnerDrivers />} />
          <Route path="/owner/dashboard" element={<CabOwnerDashboard />} />
          <Route path="/cab-owner/edit-profile" element={<CabOwnerProfileEdit />} />
          <Route path="/cab-owner/edit-account" element={<EditCabAccount />} />
          <Route path="/cab-owner/add-vehicle" element={<CabOwnerOnboarding isAddVehicleFastPath={true} />} />
        </Route>
        
        {/* ================= DRIVER ================= */}
        <Route element={<AuthGuard allowedRoles={["ALT_DRIVER", "ALTING_DRIVER", "ADMIN", "SUPER_ADMIN"]} />}>
          <Route path="/alting-driver/dashboard" element={<AltingDriverDashboard />} />
        </Route>

        {/* ================= ONBOARDING DESK (Oct 2026) ================= */}
        <Route element={<AuthGuard allowedRoles={["ONBOARDING_AGENT", "ADMIN", "SUPER_ADMIN"]} />}>
          <Route path="/desk" element={<Desk />} />
          <Route path="/desk/whatsapp" element={<DeskWhatsApp />} />
          <Route path="/desk/whatsapp/:id" element={<DeskWhatsAppThread />} />
          <Route path="/desk/:id" element={<DeskApplication />} />
        </Route>

        {/* ================= HR (Super Admin only) ================= */}
        <Route element={<AuthGuard allowedRoles={["SUPER_ADMIN"]} />}>
          <Route path="/admin/hr" element={<AdminHr />} />
          <Route path="/admin/hr/new" element={<AdminHrNew />} />
          <Route path="/admin/hr/:id" element={<AdminHrPerson />} />
        </Route>

        {/* ================= ADMIN ================= */}
        <Route element={<AuthGuard allowedRoles={["ADMIN", "SUPER_ADMIN"]} />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/pricing" element={<AdminPriceController />} />
          <Route path="/admin/cities" element={<AdminCities />} />
          <Route path="/admin/doc-names" element={<AdminDocNames />} />
          <Route path="/admin/recommended" element={<AdminRecommended />} />
          <Route path="/admin/services" element={<AdminServices />} />
          <Route path="/admin/platform-config" element={<AdminPlatformConfig />} />
          <Route path="/admin/service-fees" element={<AdminFeeManagement />} />
          <Route path="/admin/cars" element={<AdminCarApproval />} />
          <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
          <Route path="/admin/audit-logs/:auditId" element={<AdminAuditEntry />} />
          <Route path="/admin/accounts" element={<AdminAccounts />} />
          <Route path="/admin/accounts/new" element={<AdminAccountNew />} />
          <Route path="/admin/accounts/:accountKey" element={<AdminAccountDetail />} />
          <Route path="/admin/recycle-bin" element={<AdminRecycleBin />} />
          <Route path="/admin/search" element={<AdminSearch />} />
          <Route path="/admin/wallet-points" element={<AdminWalletPoints />} />
          <Route path="/admin/wallets/:key" element={<AdminWalletDetail />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/analytics/sessions/:sessionId" element={<AdminAnalyticsSession />} />
          <Route path="/admin/whatsapp-sessions" element={<AdminWhatsAppSessions />} />
          <Route path="/admin/owners" element={<AdminOwners />} />
          <Route path="/admin/owners/:onboardingId" element={<AdminOwnerDetail />} />
          <Route path="/admin/car-divergence" element={<AdminCarDivergence />} />
          <Route path="/admin/onboardings" element={<AdminOnboardings />} />
          <Route path="/admin/onboardings/:onboardingId" element={<AdminOnboardingDetail />} />
          <Route path="/admin/cab-modifications" element={<AdminCabModifications />} />
          <Route path="/admin/cab-modifications/:changeId" element={<AdminChangeDetail />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/leads/:leadId" element={<AdminLeadDetail />} />
          <Route path="/admin/alting-drivers" element={<AdminAltingDrivers />} />
          <Route path="/admin/alting-drivers/:driverId" element={<AdminAltingDriverDetail />} />
          {/* Removed October 2026: Disputes and City launch showed made-up data; the old
              driver approval, the old user list and the category keywords page no longer
              worked. A saved link to any of them opens the Admin page. */}
          {["/admin/disputes", "/admin/cities", "/admin/drivers", "/admin/users", "/admin/categorization"].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/admin" replace />} />
          ))}
        </Route>

        {/* ================= FALLBACK ================= */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
  );
}
